import { ApolloError, SingleExecutionResult } from '@apollo/client';
import { ErrorPage, LoadingPage, Modal, ModalForm, TControlledFormProps } from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import { useCallback } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { isAddressValid } from '../../../entities/address/shared';
import { TCreateEmployeeMutation, TCreateEmployeeMutationInput, useCreateEmployeeMutation, useRefreshCompanyEmployeeCountMutation } from '../../../generated/graphql';
import { usePrivateCompanyContext } from '../../../routes/outlets/PrivateCompanyOutlet';
import { ACCEPT_ADDRESS_PATH, EMPLOYEES_PATH, EMPLOYEE_ACCOUNT_PATH, EMPLOYEE_PATH } from '../../../routes/paths';
import { useEmployeeForm } from './useEmployeeForm';

export const EmployeeCreateForm = () => {
  const navigate = useNavigate();
  const company = usePrivateCompanyContext();

  const [create] = useCreateEmployeeMutation();
  const [refreshEmployeeCount] = useRefreshCompanyEmployeeCountMutation();

  const closeModal = useCallback(() => {
    navigate(generatePath(EMPLOYEES_PATH));
  }, [navigate]);

  const onSuccess = useCallback(
    async (data: SingleExecutionResult<TCreateEmployeeMutation>) => {
      if (data.errors?.length)
        for (const error of data.errors) {
          console.error(serializeError(error));
          toast.error(error.message);
        }
      else if (!data.data?.createEmployee?.id) toast.error('The server did not confirm that the employee was created. Check the employee list before trying again.');
      else {
        if (data.data?.createEmployee?.id) {
          try {
            await refreshEmployeeCount({ variables: { id: company.id } });
          } catch (error) {
            console.error(serializeError(error));
            toast.warning('The employee was created, but the company employee count could not be refreshed.');
          }
        }
        const address = data.data?.createEmployee?.address || {};

        if (data.data?.createEmployee?.id && isAddressValid(address, { checkGoogleValidated: true }))
          // SHOW OPTION TO CHOOSE ADDRESS VALIDATED BY GOOGLE
          navigate(
            generatePath(`${EMPLOYEES_PATH}/${EMPLOYEE_PATH}/${ACCEPT_ADDRESS_PATH}`, {
              employeeId: data.data.createEmployee.id
            })
          );
        else {
          if (!data.data?.createEmployee?.officeId)
            if (isAddressValid(address, { checkMissingInfo: true })) toast.error('The employee was created but the address is missing some required fields. While these fields are not filled, gifts will not be sent to this address.');
            else if (isAddressValid(address, { checkGoogleValidated: true }))
              toast.warning('The employee was successfully created but the address could not be validated by Google. Make sure it is correct otherwise it might be returned when sent.');

          if (data.data?.createEmployee?.id)
            navigate(
              generatePath(`${EMPLOYEES_PATH}/${EMPLOYEE_PATH}/${EMPLOYEE_ACCOUNT_PATH}`, {
                employeeId: data.data.createEmployee.id
              })
            );
          else closeModal();
        }
      }
    },
    [closeModal, company.id, navigate, refreshEmployeeCount]
  );

  const onSubmitError = useCallback((error: unknown) => {
    console.error(serializeError(error));
    if (error instanceof ApolloError && error.graphQLErrors.length > 0)
      for (const err of error.graphQLErrors) {
        toast.error(err.message);
      }
    else toast.error(error instanceof Error && error.message ? error.message : 'The employee could not be created. Please try again.');
  }, []);

  const onSubmit = useCallback<TControlledFormProps<TCreateEmployeeMutationInput>['onValidSubmit']>(
    (formData) => {
      if (company.id)
        return create({
          variables: {
            input: {
              ...formData,
              companyId: company.id
            }
          }
        })
          .then(onSuccess)
          .catch(onSubmitError);
    },
    [company.id, create, onSubmitError, onSuccess]
  );

  const { employeeFormFields, employeeSchema, onInvalidSubmit, loading, error } = useEmployeeForm<TCreateEmployeeMutationInput>();

  if (loading)
    return (
      <Modal closeModal={closeModal}>
        <LoadingPage />
      </Modal>
    );

  if (error) return <ErrorPage error={error} />;

  return (
    <ModalForm
      closeModal={closeModal}
      controlledFormProps={{
        fields: employeeFormFields,
        formProps: {
          resolver: joiResolver(employeeSchema, {
            convert: true,
            abortEarly: false,
            stripUnknown: false
          })
        },
        onValidSubmit: onSubmit,
        onInvalidSubmit
      }}
      headline='Create Employee'
      size='large'
    />
  );
};
